import { EditorView, keymap, lineNumbers, highlightActiveLine, highlightActiveLineGutter, placeholder } from "@codemirror/view"
import { EditorState, Compartment } from "@codemirror/state"
import { sql, SQLDialect } from "@codemirror/lang-sql"
import { linter, lintGutter } from "@codemirror/lint"
import { defaultKeymap, history, historyKeymap } from "@codemirror/commands"
import { bracketMatching, syntaxHighlighting, defaultHighlightStyle } from "@codemirror/language"
import { autocompletion, closeBrackets } from "@codemirror/autocomplete"
import { highlightSelectionMatches, searchKeymap } from "@codemirror/search"

// SOQL-specific keywords beyond standard SQL
const SOQL_KEYWORDS = [
  "TYPEOF", "END", "USING SCOPE", "DATA CATEGORY", "WITH",
  "FOR VIEW", "FOR REFERENCE", "FOR UPDATE",
  "NULLS FIRST", "NULLS LAST", "INCLUDES", "EXCLUDES",
  "ROLLUP", "CUBE", "GROUPING",
  "FIELDS(ALL)", "FIELDS(STANDARD)", "FIELDS(CUSTOM)",
  "convertCurrency", "toLabel", "FORMAT",
  // Date literals
  "YESTERDAY", "TODAY", "TOMORROW",
  "LAST_WEEK", "THIS_WEEK", "NEXT_WEEK",
  "LAST_MONTH", "THIS_MONTH", "NEXT_MONTH",
  "LAST_90_DAYS", "NEXT_90_DAYS",
  "THIS_QUARTER", "LAST_QUARTER", "NEXT_QUARTER",
  "THIS_YEAR", "LAST_YEAR", "NEXT_YEAR",
  "THIS_FISCAL_QUARTER", "LAST_FISCAL_QUARTER", "NEXT_FISCAL_QUARTER",
  "THIS_FISCAL_YEAR", "LAST_FISCAL_YEAR", "NEXT_FISCAL_YEAR",
  "LAST_N_DAYS", "NEXT_N_DAYS",
  "LAST_N_WEEKS", "NEXT_N_WEEKS",
  "LAST_N_MONTHS", "NEXT_N_MONTHS",
  "LAST_N_QUARTERS", "NEXT_N_QUARTERS",
  "LAST_N_YEARS", "NEXT_N_YEARS",
  "LAST_N_FISCAL_QUARTERS", "NEXT_N_FISCAL_QUARTERS",
  "LAST_N_FISCAL_YEARS", "NEXT_N_FISCAL_YEARS",
  // Date functions
  "CALENDAR_MONTH", "CALENDAR_QUARTER", "CALENDAR_YEAR",
  "DAY_IN_MONTH", "DAY_IN_WEEK", "DAY_IN_YEAR", "DAY_ONLY",
  "FISCAL_MONTH", "FISCAL_QUARTER", "FISCAL_YEAR",
  "HOUR_IN_DAY", "WEEK_IN_MONTH", "WEEK_IN_YEAR",
  // Aggregate
  "COUNT", "COUNT_DISTINCT", "SUM", "AVG", "MIN", "MAX",
].join(" ")

const soqlDialect = SQLDialect.define({
  keywords: SOQL_KEYWORDS + " SELECT FROM WHERE AND OR NOT IN LIKE ORDER BY ASC DESC GROUP BY HAVING LIMIT OFFSET WITH NULL TRUE FALSE",
  builtin: "Id Name CreatedDate LastModifiedDate SystemModstamp CreatedById LastModifiedById IsDeleted",
  types: "string integer double boolean date datetime time id reference currency percent phone email url textarea picklist multipicklist",
})

const languageCompartment = new Compartment()

function buildLanguageExtension(schema) {
  return languageCompartment.of(
    sql({
      dialect: soqlDialect,
      schema: schema,
      upperCaseKeywords: true,
    })
  )
}

function soqlLinter() {
  return linter((view) => {
    const query = view.state.doc.toString().trim()
    const diagnostics = []

    if (!query) return diagnostics

    // Check for SELECT-only
    const firstKeyword = query.split(/\s+/)[0]?.toUpperCase()
    if (firstKeyword && firstKeyword !== "SELECT") {
      diagnostics.push({
        from: 0,
        to: firstKeyword.length,
        severity: "error",
        message: "Only SELECT statements are supported",
      })
      return diagnostics
    }

    // Check for LIMIT (skip for aggregate queries — SOQL doesn't allow LIMIT on aggregates)
    const isAggregate = /\bSELECT\b.*\b(?:COUNT|SUM|AVG|MIN|MAX|COUNT_DISTINCT)\s*\(/i.test(query)
    const hasLimit = /\bLIMIT\s+\d+/i.test(query)
    if (!hasLimit && !isAggregate && query.length > 10) {
      diagnostics.push({
        from: query.length - 1,
        to: query.length,
        severity: "info",
        message: "No LIMIT clause — LIMIT 200 will be applied automatically",
      })
    }

    // Check if LIMIT exceeds max
    const limitMatch = query.match(/\bLIMIT\s+(\d+)/i)
    if (limitMatch) {
      const limitValue = parseInt(limitMatch[1], 10)
      if (limitValue > 2000) {
        const start = query.toUpperCase().lastIndexOf("LIMIT")
        diagnostics.push({
          from: start,
          to: start + limitMatch[0].length,
          severity: "warning",
          message: "LIMIT will be capped at 2000",
        })
      }
    }

    return diagnostics
  })
}

const editorTheme = EditorView.theme({
  "&": {
    fontSize: "14px",
    minHeight: "160px",
    maxHeight: "400px",
  },
  ".cm-scroller": {
    overflow: "auto",
    fontFamily: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
  },
  ".cm-content": {
    padding: "12px 0",
  },
  ".cm-gutters": {
    backgroundColor: "#f9fafb",
    borderRight: "1px solid #e5e7eb",
    color: "#9ca3af",
  },
  "&.cm-focused": {
    outline: "none",
  },
  ".cm-activeLine": {
    backgroundColor: "#f3f4f610",
  },
})

/**
 * Creates a CodeMirror SOQL editor.
 *
 * @param {Object} options
 * @param {HTMLElement} options.parent - Container element
 * @param {Object} options.schema - Initial schema: { ObjectName: ["field1", "field2"] }
 * @param {Function} options.onRun - Callback when Ctrl/Cmd+Enter is pressed
 * @param {Function} options.onChange - Callback with query text on change
 * @returns {EditorView} The editor view, extended with an updateSchema method
 */
export function createSoqlEditor({ parent, schema = {}, initialDoc = "", onRun, onChange }) {
  const runKeymap = keymap.of([
    {
      key: "Mod-Enter",
      run: () => {
        if (onRun) onRun()
        return true
      },
    },
  ])

  const changeListener = EditorView.updateListener.of((update) => {
    if (update.docChanged && onChange) {
      onChange(update.state.doc.toString())
    }
  })

  const state = EditorState.create({
    doc: initialDoc,
    extensions: [
      lineNumbers(),
      highlightActiveLine(),
      highlightActiveLineGutter(),
      history(),
      bracketMatching(),
      closeBrackets(),
      autocompletion(),
      highlightSelectionMatches(),
      syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
      buildLanguageExtension(schema),
      soqlLinter(),
      lintGutter(),
      runKeymap,
      changeListener,
      keymap.of([...defaultKeymap, ...historyKeymap, ...searchKeymap]),
      editorTheme,
      placeholder("SELECT Id, Name, Email FROM Contact WHERE CreatedDate = LAST_N_DAYS:30 LIMIT 100"),
      EditorView.lineWrapping,
    ],
  })

  const view = new EditorView({ state, parent })

  // Attach updateSchema method so the Stimulus controller can refresh completions
  view.updateSchema = (newSchema) => {
    view.dispatch({
      effects: languageCompartment.reconfigure(
        sql({
          dialect: soqlDialect,
          schema: newSchema,
          upperCaseKeywords: true,
        })
      ),
    })
  }

  return view
}
